import React, { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import { PlusCircle, Tags, X, CheckCircle2 } from "lucide-react";
import "../Inventory/InventoryAccordion.css";
import "./GlobalInventoryModals.css"
import MyContext from "../../../MyContext";

const AddInventorySubCategoryModal = ({
  show,
  onClose,
  userID,
  baseUrl,
  selectedCategoryName, // prefilled & non-editable
  onSuccess,
}) => {
  const { config } = useContext(MyContext);

  const [form, setForm] = useState({
    subCategory: "",
    category: selectedCategoryName || "",
    subCategoryImage: "", // URL or base64; will be uploaded if base64
  });
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setForm((f) => ({ ...f, category: selectedCategoryName || "" })); // lock category
    setPreview(
      form.subCategoryImage && form.subCategoryImage.startsWith("data:")
        ? form.subCategoryImage
        : ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedCategoryName]);

  if (!show) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "category") return; // locked
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFileToBase64 = async (file) => {
    if (!file) return;
    const toBase64 = (f) =>
      new Promise((res, rej) => {
        const reader = new FileReader();
        reader.readAsDataURL(f);
        reader.onload = () => res(reader.result);
        reader.onerror = (err) => rej(err);
      });
    try {
      const b64 = await toBase64(file);
      setForm((f) => ({ ...f, subCategoryImage: b64 }));
      setPreview(b64);
    } catch (err) {
      console.error("Image encode error:", err);
      Swal.fire({ icon: "error", title: "Image Error", text: "Failed to read image file." });
    }
  };

  // EXACT upload flow from CreateOrderModal
  const uploadImageIfNeeded = async () => {
    if (form.subCategoryImage && !form.subCategoryImage.startsWith("data:")) {
      return form.subCategoryImage || "";
    }
    if (!form.subCategoryImage || !form.subCategoryImage.startsWith("data:")) return "";

    const requestBody = {
      userid: userID,
      item_name: form.subCategory || "Subcategory",
      type: "IMG",
      image_data: form.subCategoryImage,
    };

    const resp = await axios.post(
      "https://bumppy.in/anlook/api/v1/uploadItemImage.php",
      requestBody,
      config
    );

    if (resp?.data?.status === "0" && resp?.data?.image_url) {
      return resp.data.image_url;
    }
    throw new Error(resp?.data?.message || "Image upload failed");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.category || !form.subCategory.trim()) {
      Swal.fire({ icon: "warning", title: "Missing", text: "Subcategory and Category are required." });
      return;
    }

    try {
      setSubmitting(true);

      const imageUrl = await uploadImageIfNeeded(); // <- key point

      const payload = {
        userid: String(userID),
        subCategory: form.subCategory.trim(),
        category: form.category, // locked
        subCategoryImage: imageUrl,
      };

      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0 /addInventorySubCategory`,
        payload,
        config
      );

      if (res?.data?.responseCode === "00") {
        Swal.fire({
          icon: "success",
          title: "Subcategory Added",
          text: res?.data?.responseMessage || "Done!",
        });
        onClose?.();
        onSuccess?.(); // parent can refresh lists
        setForm({
          subCategory: "",
          category: selectedCategoryName || "",
          subCategoryImage: "",
        });
        setPreview("");
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: res?.data?.responseMessage || "Something went wrong.",
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Server error." });
    } finally {
      setSubmitting(false);
    }
  };

return (
  <div className="global-inventory-modal-overlay">
    <div className="global-inventory-modal">
      <div className="global-inventory-modal-header">
        <div className="global-inventory-modal-title">
          <div className="global-inventory-modal-icon">
            <PlusCircle size={16} />
          </div>
          <h4>Add Subcategory</h4>
        </div>

        <button
          type="button"
          className="global-inventory-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="global-inventory-modal-body">
        <div className="global-inventory-section">
          <div className="global-inventory-section-head">
            <Tags size={16} />
            <span>Details</span>
            <span className="gi-sub">Subcategory + optional image</span>
          </div>

          <div className="global-inventory-grid-2">
            <div className="global-inventory-field">
              <div className="global-inventory-label">Category (locked)</div>
              <input
                className="global-inventory-input"
                type="text"
                name="category"
                value={form.category}
                readOnly
                disabled
                title="Prefilled from selected category"
              />
            </div>

            <div className="global-inventory-field">
              <div className="global-inventory-label">
                Subcategory <span className="global-inventory-req">*</span>
              </div>
              <input
                className="global-inventory-input"
                type="text"
                name="subCategory"
                value={form.subCategory}
                onChange={handleChange}
                placeholder="e.g., Ketchup"
                required
              />
            </div>
          </div>

          <div className="global-inventory-grid-2" style={{ marginTop: 12 }}>
            <div className="global-inventory-field">
              <div className="global-inventory-label">Image URL</div>
              <input
                className="global-inventory-input"
                type="text"
                name="subCategoryImage"
                value={
                  form.subCategoryImage && !form.subCategoryImage.startsWith("data:")
                    ? form.subCategoryImage
                    : ""
                }
                onChange={(e) => {
                  setForm((f) => ({ ...f, subCategoryImage: e.target.value }));
                  setPreview("");
                }}
                placeholder="https://… (optional)"
              />
            </div>

            <div className="global-inventory-field">
              <div className="global-inventory-label">Upload Image</div>
              <input
                className="global-inventory-input"
                type="file"
                accept="image/*"
                onChange={(e) => handleFileToBase64(e.target.files?.[0])}
              />
              <div className="global-inventory-note">
                If you upload, it will be stored and saved as URL.
              </div>
            </div>
          </div>

          {preview ? (
            <div className="global-inventory-preview">
              <img src={preview} alt="preview" />
            </div>
          ) : null}
        </div>

        <div className="global-inventory-footer">
          <button
            type="button"
            className="global-inventory-btn"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={16} />
            Cancel
          </button>

          <button
            type="submit"
            className="global-inventory-btn global-inventory-btn-primary"
            disabled={submitting}
          >
            <CheckCircle2 size={16} />
            {submitting ? "Adding…" : "Add Subcategory"}
          </button>
        </div>
      </form>
    </div>
  </div>
);

};

export default AddInventorySubCategoryModal;
